/**
 * Documents API service for backend document persistence.
 * Handles CRUD operations for documents stored in DynamoDB + S3 via Lambda.
 */

import type { SavedDocument, DocumentCategory } from '@/lib/types';
import { BaseApiService } from './BaseApiService';
import { API_URL } from '@/lib/apiConfig';

// API response types
export interface DocumentApiResponse {
  id: string;
  title: string;
  content?: string;
  category: DocumentCategory;
  templateType: string;
  templateName?: string;
  contentPreview?: string;
  createdAt: string;
  updatedAt: string;
  projectId?: string;
  projectName?: string;
  transcriptionId?: string;
  attachments?: { photoCount: number; noteCount: number };
}

export interface DocumentSource {
  type: 'audio' | 'photo' | 'transcript' | 'note';
  filename: string;
  url: string;
  size: number;
  contentType: string;
}

export interface ListDocumentsResponse {
  documents: DocumentApiResponse[];
  total: number;
}

export interface DocumentCountsResponse {
  counts: Record<DocumentCategory, number>;
  total: number;
}

export interface CreateDocumentRequest {
  content: string;
  category: DocumentCategory;
  templateType: string;
  title?: string;
  projectId?: string;
  projectName?: string;
  transcriptionId?: string;
}

export interface UpdateDocumentRequest {
  title?: string;
  content?: string;
  projectId?: string;
  projectName?: string;
}

/**
 * Convert API response to SavedDocument format used by frontend
 */
function toSavedDocument(apiDoc: DocumentApiResponse): SavedDocument {
  return {
    id: apiDoc.id,
    title: apiDoc.title,
    content: apiDoc.content || apiDoc.contentPreview || '',
    category: apiDoc.category,
    templateType: apiDoc.templateType,
    templateName: apiDoc.templateName,
    createdAt: apiDoc.createdAt,
    updatedAt: apiDoc.updatedAt,
    projectId: apiDoc.projectId,
    projectName: apiDoc.projectName,
    transcriptionId: apiDoc.transcriptionId,
    attachments: apiDoc.attachments,
  };
}

class DocumentsApiService extends BaseApiService {
  private identityId: string | null = null;

  constructor() {
    super(API_URL, 'DocumentsApi');
  }

  setIdentityId(identityId: string) {
    this.identityId = identityId;
  }

  /** Append identityId to a URLSearchParams instance for DynamoDB consistency. */
  private appendIdentityParam(params: URLSearchParams): void {
    if (this.identityId) params.set('identityId', this.identityId);
  }

  /**
   * List documents for the authenticated user with optional filters.
   */
  async listDocuments(options?: {
    projectId?: string;
    category?: DocumentCategory;
    limit?: number;
    search?: string;
    sortBy?: 'createdAt' | 'updatedAt' | 'title';
    sortOrder?: 'asc' | 'desc';
  }): Promise<SavedDocument[]> {
    this.log.debug('Listing documents', { action: 'listDocuments', ...options });
    const headers = await this.getHeaders();

    const params = new URLSearchParams();
    this.appendIdentityParam(params);
    if (options?.projectId) params.set('projectId', options.projectId);
    if (options?.category) params.set('category', options.category);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.search) params.set('search', options.search);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);

    const queryString = params.toString();
    const url = `${this.baseUrl}/documents${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to list documents', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to list documents');
    }

    const data: ListDocumentsResponse = await response.json();
    this.log.info('Documents listed', { action: 'listDocuments', count: data.documents.length });
    return data.documents.map(toSavedDocument);
  }

  async getAllDocuments(): Promise<SavedDocument[]> {
    return this.listDocuments();
  }

  async getDocumentsByCategory(category: DocumentCategory): Promise<SavedDocument[]> {
    return this.listDocuments({ category });
  }

  async getDocumentsByProject(projectId: string): Promise<SavedDocument[]> {
    return this.listDocuments({ projectId });
  }

  async getDocumentsByProjectAndCategory(
    projectId: string,
    category: DocumentCategory
  ): Promise<SavedDocument[]> {
    return this.listDocuments({ projectId, category });
  }

  /**
   * Get a single document by ID with full content.
   */
  async getDocumentById(documentId: string): Promise<SavedDocument | null> {
    this.log.debug('Getting document', { action: 'getDocumentById', documentId });
    const headers = await this.getHeaders();

    const idParam = this.identityId ? `?identityId=${encodeURIComponent(this.identityId)}` : '';
    const response = await fetch(`${this.baseUrl}/documents/${documentId}${idParam}`, {
      method: 'GET',
      headers,
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get document', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get document');
    }

    const data: DocumentApiResponse = await response.json();
    return toSavedDocument(data);
  }

  /**
   * Get original media sources (audio, photos, notes, transcripts) for a document.
   */
  async getDocumentSources(documentId: string): Promise<DocumentSource[]> {
    this.log.debug('Getting document sources', { action: 'getDocumentSources', documentId });
    const headers = await this.getHeaders();

    const params = new URLSearchParams();
    this.appendIdentityParam(params);
    const qs = params.toString();
    const url = `${this.baseUrl}/documents/${documentId}/sources${qs ? `?${qs}` : ''}`;

    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get document sources', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get document sources');
    }

    const data: { sources: DocumentSource[] } = await response.json();
    this.log.info('Document sources loaded', { action: 'getDocumentSources', count: data.sources.length });
    return data.sources;
  }

  /**
   * Save a new document.
   */
  async saveDocument(
    content: string,
    category: DocumentCategory,
    templateType: string,
    customTitle?: string,
    projectId?: string,
    projectName?: string
  ): Promise<SavedDocument> {
    this.log.debug('Saving document', { action: 'saveDocument', category, templateType, projectId });
    const headers = await this.getHeaders();

    const request: CreateDocumentRequest & { identityId?: string } = {
      content,
      category,
      templateType,
      title: customTitle,
      projectId,
      projectName,
    };
    if (this.identityId) request.identityId = this.identityId;

    const response = await fetch(`${this.baseUrl}/documents`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to save document', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to save document');
    }

    const data: DocumentApiResponse = await response.json();
    this.log.info('Document saved', { action: 'saveDocument', id: data.id });
    return toSavedDocument(data);
  }

  /**
   * Update an existing document.
   */
  async updateDocument(
    documentId: string,
    updates: Partial<Pick<SavedDocument, 'title' | 'content' | 'projectId' | 'projectName'>>
  ): Promise<SavedDocument | null> {
    this.log.debug('Updating document', { action: 'updateDocument', documentId });
    const headers = await this.getHeaders();

    const request: UpdateDocumentRequest & { identityId?: string } = {
      title: updates.title,
      content: updates.content,
      projectId: updates.projectId,
      projectName: updates.projectName,
    };
    if (this.identityId) request.identityId = this.identityId;

    const response = await fetch(`${this.baseUrl}/documents/${documentId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(request),
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to update document', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to update document');
    }

    const data: DocumentApiResponse = await response.json();
    return toSavedDocument(data);
  }

  /**
   * Delete a document.
   */
  async deleteDocument(documentId: string): Promise<boolean> {
    this.log.debug('Deleting document', { action: 'deleteDocument', documentId });
    const headers = await this.getHeaders();

    const idParam = this.identityId ? `?identityId=${encodeURIComponent(this.identityId)}` : '';
    const response = await fetch(`${this.baseUrl}/documents/${documentId}${idParam}`, {
      method: 'DELETE',
      headers,
    });

    if (response.status === 404) {
      return false;
    }

    if (!response.ok && response.status !== 204) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to delete document', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to delete document');
    }

    this.log.info('Document deleted', { action: 'deleteDocument', documentId });
    return true;
  }

  /**
   * Get document counts by category, optionally filtered by project.
   */
  async getDocumentCounts(projectId?: string): Promise<Record<DocumentCategory, number>> {
    this.log.debug('Getting document counts', { action: 'getDocumentCounts', projectId });
    const headers = await this.getHeaders();

    const countParams = new URLSearchParams();
    this.appendIdentityParam(countParams);
    if (projectId) countParams.set('projectId', projectId);
    const countQs = countParams.toString();
    const response = await fetch(`${this.baseUrl}/documents/counts${countQs ? `?${countQs}` : ''}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get document counts', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get document counts');
    }

    const data: DocumentCountsResponse = await response.json();
    return data.counts;
  }

  /**
   * Bulk sync documents from localStorage to cloud.
   */
  async syncDocuments(documents: Array<{
    id: string;
    title: string;
    content: string;
    category: DocumentCategory;
    templateType: string;
    createdAt: string;
    updatedAt: string;
    projectId?: string;
    projectName?: string;
  }>): Promise<{
    syncedCount: number;
    createdCount: number;
    updatedCount: number;
    skippedCount: number;
    failedCount: number;
    errors: Array<{ docId: string; error: string }>;
    conflicts: Array<{ docId: string; reason: string }>;
  }> {
    this.log.debug('Syncing documents', { action: 'syncDocuments', count: documents.length });
    const headers = await this.getHeaders();

    const syncBody: { documents: typeof documents; identityId?: string } = { documents };
    if (this.identityId) syncBody.identityId = this.identityId;

    const response = await fetch(`${this.baseUrl}/documents/sync`, {
      method: 'POST',
      headers,
      body: JSON.stringify(syncBody),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to sync documents', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to sync documents');
    }

    const data = await response.json();
    this.log.info('Documents synced', {
      action: 'syncDocuments',
      synced: data.syncedCount,
      failed: data.failedCount,
    });
    return data;
  }

  /**
   * Get document counts grouped by template type (output format).
   */
  async getDocumentCountsByTemplateType(projectId?: string): Promise<Record<string, number>> {
    this.log.debug('Getting document counts by template type', { action: 'getDocumentCountsByTemplateType', projectId });
    const headers = await this.getHeaders();

    const params = new URLSearchParams({ groupBy: 'templateType' });
    this.appendIdentityParam(params);
    if (projectId) params.set('projectId', projectId);

    const response = await fetch(`${this.baseUrl}/documents/counts?${params.toString()}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get document counts by template type', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get document counts by template type');
    }

    const data = await response.json();
    return data.countsByTemplateType;
  }

  /**
   * Get document counts grouped by project.
   */
  async getDocumentCountsByProject(): Promise<Record<string, Record<DocumentCategory, number>>> {
    this.log.debug('Getting document counts by project', { action: 'getDocumentCountsByProject' });
    const headers = await this.getHeaders();

    const projParams = new URLSearchParams({ groupBy: 'project' });
    this.appendIdentityParam(projParams);
    const response = await fetch(`${this.baseUrl}/documents/counts?${projParams.toString()}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get document counts by project', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get document counts by project');
    }

    const data = await response.json();
    return data.countsByProject;
  }

  /**
   * Get total document counts per project (simple totals for sidebar badges).
   */
  async getDocumentTotalsByProject(): Promise<Record<string, number>> {
    this.log.debug('Getting document totals by project', { action: 'getDocumentTotalsByProject' });
    const headers = await this.getHeaders();

    const pidParams = new URLSearchParams({ groupBy: 'projectId' });
    this.appendIdentityParam(pidParams);
    const response = await fetch(`${this.baseUrl}/documents/counts?${pidParams.toString()}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get document totals by project', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get document totals by project');
    }

    const data = await response.json();
    return data.countsByProjectId;
  }
}

// Export singleton instance
export const documentsApi = new DocumentsApiService();

// Export class for testing or custom instances
export { DocumentsApiService };
